import { Badge } from "@/components/ui/badge";
import { MaintenanceStatus, MaintenancePriority } from "@/types/types";

/**
 * Get the appropriate badge variant for maintenance status
 */
export function getMaintenanceStatusVariant(
  status: MaintenanceStatus
): "default" | "secondary" | "outline" | "destructive" | "success" | "warning" {
  switch (status) {
    case "completed":
      return "success"; // Green
    case "in-progress":
      return "default"; // Blue
    case "scheduled":
      return "secondary"; // Gray
    case "pending":
      return "warning"; // Yellow
    case "cancelled":
      return "secondary"; // Dull gray
    default:
      return "outline";
  }
}

/**
 * Format maintenance status for display
 */
export function formatMaintenanceStatus(status: MaintenanceStatus): string {
  if (!status || typeof status !== "string") {
    return "Unknown";
  }

  switch (status) {
    case "pending":
      return "Pending";
    case "scheduled":
      return "Scheduled";
    case "in-progress":
      return "In Progress";
    case "completed":
      return "Completed";
    case "cancelled":
      return "Cancelled";
    default:
      const statusStr = String(status);
      return statusStr
        .split("-")
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
  }
}

/**
 * Render a status badge for maintenance request
 */
export function MaintenanceStatusBadge({
  status,
}: {
  status: MaintenanceStatus | any;
}) {
  // Handle case where status might be an object due to data corruption
  const statusValue: MaintenanceStatus =
    typeof status === "string"
      ? (status as MaintenanceStatus)
      : (status?.status as MaintenanceStatus) || "pending";

  return (
    <Badge variant={getMaintenanceStatusVariant(statusValue)}>
      {formatMaintenanceStatus(statusValue)}
    </Badge>
  );
}

/**
 * Get the appropriate badge variant for maintenance priority
 */
export function getPriorityVariant(
  priority: MaintenancePriority
): "default" | "secondary" | "outline" | "destructive" | "success" | "warning" {
  switch (priority) {
    case "high":
      return "destructive"; // Red
    case "medium":
      return "warning"; // Yellow
    case "low":
      return "secondary"; // Gray
    default:
      return "outline";
  }
}

/**
 * Format priority for display
 */
export function formatPriority(priority: MaintenancePriority): string {
  return priority.charAt(0).toUpperCase() + priority.slice(1);
}

/**
 * Render a priority badge for maintenance request
 */
export function PriorityBadge({ priority }: { priority: MaintenancePriority }) {
  return (
    <Badge variant={getPriorityVariant(priority)}>
      {formatPriority(priority)}
    </Badge>
  );
}

/**
 * Get available tenant actions for a maintenance request
 */
export function getTenantActions(status: MaintenanceStatus): {
  canDelete: boolean;
  canCancel: boolean;
  canMarkResolved: boolean;
} {
  return {
    canDelete: status === "in-progress", // Delete in-progress requests
    canCancel: status === "pending", // Cancel pending requests
    canMarkResolved: status === "in-progress", // Mark in-progress as completed
  };
}
